'use client';

import { useEffect, useMemo, useState, type FC } from 'react';

import Link from '@mui/material/Link';

import { DEFAULT_DISPLAY } from '@/constants/default-value/default-value';
import useGuard from '@/hooks/useGuard';
import { useAppDispatch, useAppSelector } from '@/redux-store/hooks';
import { showEntityDetailAction } from '@/redux-store/slices/entity-detail';
import type { EntityDetailProps } from '../../types/EntityDetail.types';
import { entityTypeManager } from '../../utils/entity';
import type { Entity } from '../../utils/entity/entity';

const EntityDetailButton: FC<EntityDetailProps> = ({
    text,
    identifier,
    entityId,
    type,
}) => {
    const dispatch = useAppDispatch();

    const entityData = useMemo(
        () => entityTypeManager().create(type) as Entity,
        [type]
    );

    const hasPermission = useGuard({ routeKey: entityData?.getRouteDetail() });

    const entityDetailDict = useAppSelector(state => state.entityDetail);

    const [shouldShowDetailButton, setShouldShowDetailButton] = useState(true);

    useEffect(() => {
        const values = Object.values(entityDetailDict);

        if (values.length === 0) {
            setShouldShowDetailButton(true);

            return;
        }

        const hasSameType = values.some(detail => {
            return detail.type === type;
        });

        setShouldShowDetailButton(!hasSameType);
    }, [entityDetailDict, type]);

    const handleShowDetailModal = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        dispatch(
            showEntityDetailAction({
                entityId,
                type,
                identifier,
            })
        );
    };

    const textHasString = text !== null && text !== undefined;

    return !hasPermission ||
        !shouldShowDetailButton ||
        !textHasString ||
        !entityId ? (
        <span className='raw-text'>{text || DEFAULT_DISPLAY}</span>
    ) : (
        <Link
            component='button'
            onClick={handleShowDetailModal}
            underline='always'
            title={text}
            sx={{
                cursor: 'pointer',
                textUnderlineOffset: '2px',
                textAlign: 'left',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                textDecorationLine: 'none',
                color: '#0f2fbd!important',
                ':hover': {
                    textDecorationLine: 'underline',
                },
            }}
        >
            {text}
        </Link>
    );
};

export default EntityDetailButton;
